import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, CalendarCheck, Users, Sparkles, Plus, Share2, Trash2, ChevronLeft, ExternalLink, Copy, X, Award, Settings, CalendarDays, Globe, Lock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { DEFAULT_EVENT_IMAGE, handleEventImageError } from '../utils/imageUrl';
import {
  fetchFavorites, removeFavorite,
  fetchPlans, removePlan,
  fetchLists, createList, fetchListDetail, deleteList, removeEventFromList, updateList,
  fetchRecommendations,
  fetchMyStats,
} from '../lib/meApi';
import LevelAvatar from '../components/engagement/LevelAvatar';
import BadgesTab from '../components/engagement/BadgesTab';
import PreferencesTab from '../components/engagement/PreferencesTab';
import CalendarTab from '../components/engagement/CalendarTab';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

const EventCard = ({ event, onRemove, removeLabel = 'Quitar' }) => (
  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
    <img src={event.main_image || DEFAULT_EVENT_IMAGE} alt={event.name} className="w-14 h-14 rounded-xl object-cover shrink-0" onError={handleEventImageError} />
    <div className="flex-1 min-w-0">
      <a
        href={`/event/${event.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold text-[#064E3B] truncate block hover:text-[#00CC66] hover:underline"
        data-testid={`me-event-link-${event.id}`}
      >
        {event.name}
      </a>
      <p className="text-xs text-gray-500 mt-0.5 truncate">
        {event.is_permanent ? 'Permanente' : event.is_recurring ? 'Recurrente' : fmtDate(event.start_date)}
        {event.commune_name && ` • ${event.commune_name}`}
        {event.category_name && ` • ${event.category_name}`}
      </p>
    </div>
    {onRemove && (
      <button
        onClick={() => onRemove(event)}
        className="p-2 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors shrink-0"
        title={removeLabel}
        data-testid={`me-event-remove-${event.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    )}
  </div>
);

const EmptyState = ({ icon: Icon, title, subtitle, ctaLabel, ctaTo }) => {
  const navigate = useNavigate();
  return (
    <div className="text-center py-12">
      <Icon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
      <h3 className="text-lg font-semibold text-[#064E3B] mb-1">{title}</h3>
      <p className="text-sm text-gray-500 mb-4">{subtitle}</p>
      {ctaLabel && (
        <Button onClick={() => navigate(ctaTo || '/explore')} className="rounded-full bg-[#00CC66] hover:bg-[#00B359]">
          {ctaLabel}
        </Button>
      )}
    </div>
  );
};

// ============ Tabs ============
const FavoritesTab = () => {
  const [items, setItems] = useState(null);
  const reload = async () => {
    try { setItems(await fetchFavorites()); } catch { setItems([]); }
  };
  useEffect(() => { reload(); }, []);
  const handleRemove = async (ev) => {
    await removeFavorite(ev.id);
    setItems(items.filter(f => f.event.id !== ev.id));
    toast.success('Quitado de Favoritos');
  };
  if (items === null) return <div className="text-center py-12 text-gray-400">Cargando...</div>;
  if (items.length === 0) return (
    <EmptyState
      icon={Heart}
      title="Aún no tienes favoritos"
      subtitle="Toca el corazón en cualquier evento para guardarlo aquí."
      ctaLabel="Explorar eventos"
    />
  );
  return (
    <div className="space-y-2" data-testid="me-favorites-list">
      {items.map(f => <EventCard key={f.event.id} event={f.event} onRemove={handleRemove} removeLabel="Quitar de Favoritos" />)}
    </div>
  );
};

const PlansTab = () => {
  const [items, setItems] = useState(null);
  const reload = async () => {
    try { setItems(await fetchPlans()); } catch { setItems([]); }
  };
  useEffect(() => { reload(); }, []);
  const handleRemove = async (ev) => {
    await removePlan(ev.id);
    setItems(items.filter(p => p.event.id !== ev.id));
    toast.success('Quitado de Mis Planes');
  };
  if (items === null) return <div className="text-center py-12 text-gray-400">Cargando...</div>;
  if (items.length === 0) return (
    <EmptyState
      icon={CalendarCheck}
      title="No tienes planes guardados"
      subtitle="Marca un evento como 'Voy a ir' y lo verás aquí."
      ctaLabel="Explorar eventos"
    />
  );
  return (
    <div className="space-y-2" data-testid="me-plans-list">
      {items.map(p => (
        <div key={p.event.id} className="relative">
          <EventCard event={p.event} onRemove={handleRemove} removeLabel="Quitar de Mis Planes" />
        </div>
      ))}
    </div>
  );
};

const ListsTab = () => {
  const [lists, setLists] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmoji, setNewEmoji] = useState('📌');
  const [newDesc, setNewDesc] = useState('');
  const [openList, setOpenList] = useState(null); // detail
  const [openDetail, setOpenDetail] = useState(null);

  const reload = async () => {
    try { setLists(await fetchLists()); } catch { setLists([]); }
  };
  useEffect(() => { reload(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createList({ name: newName.trim(), emoji: newEmoji, description: newDesc.trim() || null });
      setShowCreate(false);
      setNewName('');
      setNewEmoji('📌');
      setNewDesc('');
      toast.success('Lista creada');
      reload();
    } catch { toast.error('No se pudo crear la lista'); }
  };

  const handleOpen = async (lst) => {
    setOpenList(lst);
    try { setOpenDetail(await fetchListDetail(lst.id)); } catch { setOpenDetail(null); }
  };
  const closeOpen = () => { setOpenList(null); setOpenDetail(null); };

  const handleDelete = async (lst, e) => {
    e?.stopPropagation();
    if (!window.confirm(`¿Eliminar "${lst.name}"? Esta acción no se puede deshacer.`)) return;
    await deleteList(lst.id);
    toast.success('Lista eliminada');
    reload();
  };

  const handleShare = (lst, e) => {
    e?.stopPropagation();
    const link = `${window.location.origin}/lists/join/${lst.share_token}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success('Enlace copiado al portapapeles 🎉'),
      () => toast.error('No se pudo copiar'),
    );
  };

  const handleRemoveItem = async (event) => {
    if (!openDetail) return;
    await removeEventFromList(openDetail.id, event.id);
    setOpenDetail({ ...openDetail, items: openDetail.items.filter(e => e.id !== event.id), item_count: openDetail.item_count - 1 });
    toast.success('Evento quitado de la lista');
    reload();
  };

  const handleTogglePublic = async () => {
    if (!openDetail || !openList?.is_owner) return;
    const newVal = !openDetail.is_public;
    try {
      await updateList(openDetail.id, { is_public: newVal });
      setOpenDetail({ ...openDetail, is_public: newVal });
      reload();
      toast.success(newVal ? 'Lista ahora pública 🌍' : 'Lista vuelta a privada');
    } catch {
      toast.error('No se pudo actualizar');
    }
  };

  if (lists === null) return <div className="text-center py-12 text-gray-400">Cargando...</div>;

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {lists.length} {lists.length === 1 ? 'lista' : 'listas'} · comparte planes con amigos
        </p>
        <Button onClick={() => setShowCreate(true)} className="rounded-full bg-[#00CC66] hover:bg-[#00B359]" data-testid="me-list-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva lista
        </Button>
      </div>

      {lists.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Aún no tienes listas"
          subtitle="Crea una lista para guardar eventos y compartirlos con amigos."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="me-lists-grid">
          {lists.map(lst => (
            <Card
              key={lst.id}
              className="rounded-2xl border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleOpen(lst)}
              data-testid={`me-list-card-${lst.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{lst.emoji}</span>
                    <h3 className="font-semibold text-[#064E3B] truncate">{lst.name}</h3>
                  </div>
                  {!lst.is_owner && <Badge className="bg-blue-100 text-blue-700 border-0 text-xs rounded-full shrink-0">Invitado</Badge>}
                </div>
                {lst.description && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{lst.description}</p>}
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  <span>{lst.item_count} {lst.item_count === 1 ? 'evento' : 'eventos'}</span>
                  <span>•</span>
                  <span>{lst.member_count} {lst.member_count === 1 ? 'miembro' : 'miembros'}</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={(e) => handleShare(lst, e)}>
                    <Share2 className="w-3 h-3 mr-1" /> Compartir
                  </Button>
                  {lst.is_owner && (
                    <Button size="sm" variant="ghost" className="rounded-full hover:bg-red-50 hover:text-red-500" onClick={(e) => handleDelete(lst, e)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva lista</DialogTitle>
            <DialogDescription>
              Crea una lista temática para guardar eventos y compartirla con amigos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2 items-center">
              <Input
                placeholder="📌"
                value={newEmoji}
                onChange={(e) => setNewEmoji(e.target.value.slice(0, 2))}
                className="w-16 text-center text-2xl"
                data-testid="me-list-emoji-input"
              />
              <Input
                placeholder="Ej: Panoramas con amigos"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={120}
                data-testid="me-list-name-input"
                autoFocus
              />
            </div>
            <Textarea
              placeholder="Descripción (opcional)"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              maxLength={280}
              rows={2}
              data-testid="me-list-desc-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={handleCreate} className="rounded-full bg-[#00CC66] hover:bg-[#00B359]" data-testid="me-list-create-confirm">
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!openList} onOpenChange={(o) => !o && closeOpen()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <span className="text-2xl mr-2">{openList?.emoji}</span>
              {openList?.name}
            </DialogTitle>
            {openList?.description && <DialogDescription>{openList.description}</DialogDescription>}
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{openDetail?.item_count ?? '...'} eventos · {openDetail?.member_count ?? '...'} miembros</span>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={() => openList && handleShare(openList)}
                data-testid="me-list-share-btn"
              >
                <Copy className="w-3 h-3 mr-1" /> Copiar enlace
              </Button>
            </div>
            <div className="space-y-2 mt-3">
              {openDetail?.items?.length ? (
                openDetail.items.map(ev => (
                  <EventCard key={ev.id} event={ev} onRemove={openList?.is_owner || openList?.is_collaborative ? handleRemoveItem : null} />
                ))
              ) : (
                <p className="text-center py-8 text-gray-400 text-sm">Lista vacía. Comparte el enlace para que tus amigos agreguen eventos.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const RecommendationsTab = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    fetchRecommendations(12).then(setItems).catch(() => setItems([]));
  }, []);
  if (items === null) return <div className="text-center py-12 text-gray-400">Cargando...</div>;
  if (items.length === 0) return (
    <EmptyState
      icon={Sparkles}
      title="Aún no tenemos suficientes datos"
      subtitle="Explora eventos y guarda algunos para recibir recomendaciones personalizadas."
      ctaLabel="Explorar eventos"
    />
  );
  return (
    <div className="space-y-2" data-testid="me-recommendations-list">
      <p className="text-sm text-gray-500 mb-3">
        Eventos elegidos según tus favoritos, planes y vistas recientes.
      </p>
      {items.map(ev => <EventCard key={ev.id} event={ev} />)}
    </div>
  );
};

// ============ Page ============
export default function MePage() {
  const navigate = useNavigate();
  const isLogged = !!localStorage.getItem('token');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!isLogged) navigate('/login?next=/me');
  }, [isLogged, navigate]);

  const reloadStats = () => {
    fetchMyStats().then(setStats).catch(() => setStats(null));
  };
  useEffect(() => {
    if (isLogged) reloadStats();
  }, [isLogged]);

  if (!isLogged) return null;

  const u = stats?.user;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-6 md:py-10">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" className="rounded-full -ml-2" onClick={() => navigate('/')}>
            <ChevronLeft className="w-5 h-5" />
            Volver
          </Button>
        </div>

        {/* Header with avatar + level */}
        <div className="mb-6 flex items-center gap-4" data-testid="me-header">
          {u ? (
            <LevelAvatar name={u.name} color={u.avatar_color} avatarUrl={u.avatar_url} level={u.level} size="lg" />
          ) : (
            <div className="w-[72px] h-[72px] rounded-full bg-gray-100 animate-pulse" />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-[#064E3B] truncate">
              {u?.name || 'Mi panel'}
            </h1>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              {u?.username && (
                <a
                  href={`/u/${u.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-emerald-600 hover:underline"
                  data-testid="me-public-profile-link"
                >
                  @{u.username}
                </a>
              )}
              {u && (
                <>
                  <span className="text-xs text-gray-300">•</span>
                  <span className="text-xs text-gray-600 font-medium">{u.level_name}</span>
                  <span className="text-xs text-gray-300">•</span>
                  <span className="text-xs text-emerald-700 font-bold">{u.points} pts</span>
                </>
              )}
            </div>
          </div>
        </div>

        <Tabs defaultValue="favorites" className="w-full">
          <TabsList className="grid grid-cols-7 w-full mb-6 bg-white rounded-2xl shadow-sm p-1 gap-0.5">
            <TabsTrigger value="favorites" className="rounded-xl data-[state=active]:bg-[#00CC66] data-[state=active]:text-white" data-testid="me-tab-favorites">
              <Heart className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Favoritos</span>
            </TabsTrigger>
            <TabsTrigger value="plans" className="rounded-xl data-[state=active]:bg-[#00CC66] data-[state=active]:text-white" data-testid="me-tab-plans">
              <CalendarCheck className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Planes</span>
            </TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-xl data-[state=active]:bg-[#00CC66] data-[state=active]:text-white" data-testid="me-tab-calendar">
              <CalendarDays className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Calendario</span>
            </TabsTrigger>
            <TabsTrigger value="lists" className="rounded-xl data-[state=active]:bg-[#00CC66] data-[state=active]:text-white" data-testid="me-tab-lists">
              <Users className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Listas</span>
            </TabsTrigger>
            <TabsTrigger value="recs" className="rounded-xl data-[state=active]:bg-[#00CC66] data-[state=active]:text-white" data-testid="me-tab-recommendations">
              <Sparkles className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Para ti</span>
            </TabsTrigger>
            <TabsTrigger value="badges" className="rounded-xl data-[state=active]:bg-[#00CC66] data-[state=active]:text-white" data-testid="me-tab-badges">
              <Award className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Logros</span>
            </TabsTrigger>
            <TabsTrigger value="prefs" className="rounded-xl data-[state=active]:bg-[#00CC66] data-[state=active]:text-white" data-testid="me-tab-preferences">
              <Settings className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Configuración</span>
            </TabsTrigger>
          </TabsList>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-5 md:p-6">
              <TabsContent value="favorites" className="m-0"><FavoritesTab /></TabsContent>
              <TabsContent value="plans" className="m-0"><PlansTab /></TabsContent>
              <TabsContent value="calendar" className="m-0"><CalendarTab /></TabsContent>
              <TabsContent value="lists" className="m-0"><ListsTab /></TabsContent>
              <TabsContent value="recs" className="m-0"><RecommendationsTab /></TabsContent>
              <TabsContent value="badges" className="m-0"><BadgesTab /></TabsContent>
              <TabsContent value="prefs" className="m-0"><PreferencesTab onUpdated={reloadStats} /></TabsContent>
            </CardContent>
          </Card>
        </Tabs>
      </div>
    </div>
  );
}
